import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Pencil, Copy, Trash2, GripVertical, X, ArrowLeft, Loader2, Send, BarChart3, AlertTriangle } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip as RTooltip,
  ResponsiveContainer,
  LabelList,
  Cell,
} from "recharts";
import {
  useSurveyResults,
  useSurveysInGroup,
  useAggregateSurveyResults,
  type SurveyAnswer,
} from "@/hooks/useSurveyResults";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  usePlatformSurveys,
  usePlatformSurvey,
  useCreatePlatformSurvey,
  useUpdatePlatformSurvey,
  useDuplicatePlatformSurvey,
  useDeletePlatformSurvey,
  useUpsertPlatformSurveyQuestions,
  SurveyAnswerLossError,
  useSendPlatformSurveyWave,
  type PlatformSurvey,
  type PlatformSurveyQuestion,
} from "@/hooks/usePlatformSurveys";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { supabase } from "@/integrations/supabase/client";

const FONT = { fontFamily: "Public Sans, Arial, sans-serif" } as const;
const ORANGE = "#C94F13";
const GREEN = "#8FCA3F";
const BLUE = "#29A4E3";

type Mode = "list" | "builder" | "results";

type DraftQuestion = {
  /** Present for questions already saved in the DB; undefined for new ones. */
  id?: string | null;
  question_text: string;
  question_type: PlatformSurveyQuestion["question_type"];
  options: string[] | null;
  impact_metric_key: string | null;
  group_question_key: string | null;
  display_order: number;
  required: boolean;
};

type DraftSurvey = {
  title: string;
  description: string;
  survey_type: PlatformSurvey["survey_type"];
  target_role: PlatformSurvey["target_role"];
  targeting_mode: PlatformSurvey["targeting_mode"];
  recurrence: PlatformSurvey["recurrence"];
  status: PlatformSurvey["status"];
};

const emptyDraft: DraftSurvey = {
  title: "",
  description: "",
  survey_type: "general",
  target_role: "both",
  targeting_mode: "all",
  recurrence: "once",
  status: "draft",
};

const coerce = (v: string | null | undefined) => (v && v.trim() !== "" ? v : null);

function TypeBadge({ type }: { type: PlatformSurvey["survey_type"] }) {
  const isImpact = type === "impact";
  return (
    <Badge
      style={{
        backgroundColor: isImpact ? ORANGE : BLUE,
        color: "white",
        ...FONT,
      }}
    >
      {isImpact ? "Impact" : "General"}
    </Badge>
  );
}

function StatusBadge({ status }: { status: PlatformSurvey["status"] }) {
  const map = {
    draft: { label: "Draft", color: "#6B7280" },
    active: { label: "Active", color: GREEN },
    closed: { label: "Closed", color: "#6B7280" },
  } as const;
  const cfg = map[status];
  return (
    <Badge style={{ backgroundColor: cfg.color, color: "white", ...FONT }}>
      {cfg.label}
    </Badge>
  );
}

const labelize = (s: string) =>
  s.charAt(0).toUpperCase() + s.slice(1).replace(/_/g, " ");

export default function PlatformSurveyManager() {
  const [mode, setMode] = useState<Mode>("list");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [waveSurvey, setWaveSurvey] = useState<PlatformSurvey | null>(null);
  const [resultsSurvey, setResultsSurvey] = useState<PlatformSurvey | null>(null);

  const { surveys, isLoading } = usePlatformSurveys();
  const duplicateMut = useDuplicatePlatformSurvey();
  const deleteMut = useDeletePlatformSurvey();

  const handleNew = () => {
    setEditingId(null);
    setMode("builder");
  };

  const handleEdit = (id: string) => {
    setEditingId(id);
    setMode("builder");
  };

  const handleDuplicate = async (id: string) => {
    try {
      const created = await duplicateMut.mutateAsync(id);
      toast.success("Survey duplicated.");
      setEditingId(created.id);
      setMode("builder");
    } catch (e: any) {
      console.error("Full Supabase error:", JSON.stringify(e, null, 2));
      toast.error("Failed to duplicate survey.");
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await deleteMut.mutateAsync(deleteId);
      toast.success("Survey deleted.");
    } catch (e: any) {
      console.error("Full Supabase error:", JSON.stringify(e, null, 2));
      toast.error("Failed to delete survey.");
    } finally {
      setDeleteId(null);
    }
  };

  if (mode === "builder") {
    return (
      <SurveyBuilder
        surveyId={editingId}
        onClose={() => {
          setMode("list");
          setEditingId(null);
        }}
      />
    );
  }

  if (mode === "results" && resultsSurvey) {
    return (
      <SurveyResults
        survey={resultsSurvey}
        onClose={() => {
          setMode("list");
          setResultsSurvey(null);
        }}
      />
    );
  }

  return (
    <div className="space-y-6" style={FONT}>
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Platform Surveys</h2>
          <p className="text-sm text-muted-foreground">
            Create and manage standalone surveys for users.
          </p>
        </div>
        <Button
          onClick={handleNew}
          style={{ backgroundColor: ORANGE, color: "white" }}
          className="hover:opacity-90"
        >
          <Plus className="h-4 w-4 mr-2" /> New Survey
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-6 space-y-3">
              {[...Array(3)].map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : !surveys || surveys.length === 0 ? (
            <div className="p-12 text-center text-muted-foreground">
              <p className="text-lg font-medium">No surveys yet</p>
              <p className="text-sm mt-1">
                Click "New Survey" to create your first platform survey.
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Target Role</TableHead>
                  <TableHead>Targeting</TableHead>
                  <TableHead>Recurrence</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Questions</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {surveys.map((s) => (
                  <TableRow key={s.id}>
                    <TableCell className="font-medium">{s.title}</TableCell>
                    <TableCell><TypeBadge type={s.survey_type} /></TableCell>
                    <TableCell>{labelize(s.target_role)}</TableCell>
                    <TableCell>{labelize(s.targeting_mode)}</TableCell>
                    <TableCell>{labelize(s.recurrence)}</TableCell>
                    <TableCell><StatusBadge status={s.status} /></TableCell>
                    <TableCell>{s.question_count}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button size="icon" variant="ghost" onClick={() => handleEdit(s.id)} title="Edit">
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => handleDuplicate(s.id)}
                          disabled={duplicateMut.isPending}
                          title="Duplicate"
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setWaveSurvey(s)}
                                title={s.status === "draft" ? "Survey is in draft — activate before sending" : "Send Wave"}
                                style={{
                                  color: s.status === "draft" ? "#f59e0b" : ORANGE,
                                  borderColor: s.status === "draft" ? "#f59e0b" : ORANGE,
                                }}
                              >
                                <Send className="h-4 w-4 mr-1" />
                                Send Wave
                              </Button>
                            </TooltipTrigger>
                            {s.status === "draft" && (
                              <TooltipContent>
                                Survey is in draft — activate before sending
                              </TooltipContent>
                            )}
                          </Tooltip>
                        </TooltipProvider>
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => {
                            setResultsSurvey(s);
                            setMode("results");
                          }}
                          title="View Results"
                        >
                          <BarChart3 className="h-4 w-4" style={{ color: BLUE }} />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => setDeleteId(s.id)}
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent style={FONT}>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this survey?</AlertDialogTitle>
            <AlertDialogDescription>
              This cannot be undone. All questions, assignments, and responses will be permanently removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <SendWaveDialog survey={waveSurvey} onClose={() => setWaveSurvey(null)} />
    </div>
  );
}

/* ===================== Builder ===================== */

function SurveyBuilder({
  surveyId,
  onClose,
}: {
  surveyId: string | null;
  onClose: () => void;
}) {
  const isEditing = !!surveyId;
  const { survey, questions, isLoading } = usePlatformSurvey(surveyId);
  const createMut = useCreatePlatformSurvey();
  const updateMut = useUpdatePlatformSurvey();
  const upsertQuestions = useUpsertPlatformSurveyQuestions(surveyId);

  const [draft, setDraft] = useState<DraftSurvey>(emptyDraft);
  const [draftQs, setDraftQs] = useState<DraftQuestion[]>([]);
  const [saving, setSaving] = useState(false);
  const [dragIdx, setDragIdx] = useState<number | null>(null);

  useEffect(() => {
    if (isEditing && survey && questions) {
      setDraft({
        title: survey.title,
        description: survey.description || "",
        survey_type: survey.survey_type,
        target_role: survey.target_role,
        targeting_mode: survey.targeting_mode,
        recurrence: survey.recurrence,
        status: survey.status,
      });
      setDraftQs(
        questions.map((q, i) => ({
          id: q.id,
          question_text: q.question_text,
          question_type: q.question_type,
          options: Array.isArray(q.options) ? (q.options as string[]) : null,
          impact_metric_key: q.impact_metric_key,
          group_question_key: q.group_question_key,
          display_order: i,
          required: q.required,
        }))
      );
    }
  }, [isEditing, survey, questions]);

  const addQuestion = () => {
    setDraftQs((prev) => [
      ...prev,
      {
        question_text: "",
        question_type: "text",
        options: null,
        impact_metric_key: null,
        group_question_key: null,
        display_order: prev.length,
        required: true,
      },
    ]);
  };

  const updateQuestion = (idx: number, patch: Partial<DraftQuestion>) => {
    setDraftQs((prev) =>
      prev.map((q, i) => {
        if (i !== idx) return q;
        const next = { ...q, ...patch };
        if (patch.question_type) {
          if (patch.question_type === "multiple_choice") {
            next.options = next.options ?? [""];
          } else {
            next.options = null;
          }
        }
        return next;
      })
    );
  };

  const removeQuestion = (idx: number) => {
    setDraftQs((prev) => prev.filter((_, i) => i !== idx));
  };

  const onDragStart = (idx: number) => setDragIdx(idx);
  const onDragOver = (e: React.DragEvent) => e.preventDefault();
  const onDrop = (idx: number) => {
    if (dragIdx === null || dragIdx === idx) return;
    setDraftQs((prev) => {
      const next = [...prev];
      const [moved] = next.splice(dragIdx, 1);
      next.splice(idx, 0, moved);
      return next.map((q, i) => ({ ...q, display_order: i }));
    });
    setDragIdx(null);
  };

  const [confirmActivate, setConfirmActivate] = useState(false);
  const [answerLossWarning, setAnswerLossWarning] = useState<string | null>(null);

  const performSave = async (confirmAnswerLoss = false) => {
    if (!draft.title.trim()) {
      toast.error("Title is required.");
      return;
    }
    setSaving(true);
    try {
      const payload: Partial<PlatformSurvey> = {
        title: draft.title.trim(),
        description: coerce(draft.description) as any,
        survey_type: draft.survey_type,
        target_role: draft.target_role,
        targeting_mode: draft.targeting_mode,
        recurrence: draft.recurrence,
        status: draft.status,
      };

      let activeId = surveyId;
      if (isEditing && surveyId) {
        await updateMut.mutateAsync({ id: surveyId, ...payload });
      } else {
        const created = await createMut.mutateAsync(payload);
        activeId = created.id;
      }

      const qPayload = draftQs.map((q, i) => ({
        id: q.id ?? null,
        question_text: q.question_text.trim(),
        question_type: q.question_type,
        options:
          q.question_type === "multiple_choice"
            ? (q.options || []).filter((o) => o.trim() !== "")
            : null,
        impact_metric_key:
          draft.survey_type === "impact" ? coerce(q.impact_metric_key) : null,
        group_question_key: coerce(q.group_question_key),
        display_order: i,
        required: q.required,
      }));

      // Re-bind hook target if just-created
      if (activeId && !surveyId) {
        // Use a one-off direct call by going through the hook factory
        // Simplest: reuse upsert but it's bound to surveyId. Do inline instead.
        const { supabase } = await import("@/integrations/supabase/client");
        await supabase.from("platform_survey_questions").delete().eq("survey_id", activeId);
        if (qPayload.length > 0) {
          const rows = qPayload.map((q) => ({ survey_id: activeId, ...q }));
          const { error } = await supabase.from("platform_survey_questions").insert(rows);
          if (error) throw error;
        }
      } else if (activeId) {
        await upsertQuestions.mutateAsync({
          questions: qPayload as any,
          confirmAnswerLoss,
        });
      }

      toast.success("Survey saved.");
      onClose();
    } catch (e: any) {
      // Removing questions from a survey that already has answers would
      // cascade-delete those answers. Ask before destroying results.
      if (e instanceof SurveyAnswerLossError) {
        setAnswerLossWarning(e.message);
        setSaving(false);
        return;
      }
      console.error("Full Supabase error:", JSON.stringify(e, null, 2));
      toast.error("Failed to save survey. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleSave = () => {
    // Confirm only when transitioning from Draft -> Active
    if (isEditing && survey?.status === "draft" && draft.status === "active") {
      setConfirmActivate(true);
      return;
    }
    void performSave();
  };


  if (isEditing && isLoading) {
    return (
      <div className="flex items-center justify-center py-12" style={FONT}>
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6" style={FONT}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={onClose}>
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </Button>
          <h2 className="text-2xl font-bold">
            {isEditing ? "Edit Survey" : "New Survey"}
          </h2>
        </div>
      </div>

      {/* Section 1: Settings */}
      <Card>
        <CardHeader>
          <CardTitle>Survey Settings</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Title *</Label>
            <Input
              value={draft.title}
              onChange={(e) => setDraft({ ...draft, title: e.target.value })}
              placeholder="Survey title"
            />
          </div>
          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea
              value={draft.description}
              onChange={(e) => setDraft({ ...draft, description: e.target.value })}
              placeholder="Optional description shown to respondents"
              rows={3}
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Survey Type</Label>
              <Select
                value={draft.survey_type}
                onValueChange={(v) => setDraft({ ...draft, survey_type: v as any })}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="impact">Impact</SelectItem>
                  <SelectItem value="general">General</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Target Role</Label>
              <Select
                value={draft.target_role}
                onValueChange={(v) => setDraft({ ...draft, target_role: v as any })}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="entrepreneur">Entrepreneurs</SelectItem>
                  <SelectItem value="mentor">Mentors</SelectItem>
                  <SelectItem value="both">Both</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Targeting Mode</Label>
              <Select
                value={draft.targeting_mode}
                onValueChange={(v) => setDraft({ ...draft, targeting_mode: v as any })}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Users</SelectItem>
                  <SelectItem value="cohort">Specific Cohort</SelectItem>
                  <SelectItem value="individual">Individual Users</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Recurrence</Label>
              <Select
                value={draft.recurrence}
                onValueChange={(v) => setDraft({ ...draft, recurrence: v as any })}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="once">One-time</SelectItem>
                  <SelectItem value="quarterly">Quarterly</SelectItem>
                  <SelectItem value="annually">Annually</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Status</Label>
              <Select
                value={draft.status}
                onValueChange={(v) => setDraft({ ...draft, status: v as any })}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="closed">Closed</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Section 2: Questions */}
      <Card>
        <CardHeader>
          <CardTitle>Questions</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {draftQs.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-6">
              No questions yet. Click "+ Add Question" below to start building.
            </p>
          )}
          {draftQs.map((q, idx) => (
            <div
              key={idx}
              draggable
              onDragStart={() => onDragStart(idx)}
              onDragOver={onDragOver}
              onDrop={() => onDrop(idx)}
              className="border rounded-lg p-4 space-y-3 bg-card"
            >
              <div className="flex items-start gap-3">
                <div className="cursor-grab pt-2" title="Drag to reorder">
                  <GripVertical className="h-5 w-5 text-muted-foreground" />
                </div>
                <div className="flex-1 space-y-3">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-muted-foreground w-6">
                      {idx + 1}.
                    </span>
                    <Input
                      value={q.question_text}
                      onChange={(e) =>
                        updateQuestion(idx, { question_text: e.target.value })
                      }
                      placeholder="Question text"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">Question Type</Label>
                      <Select
                        value={q.question_type}
                        onValueChange={(v) =>
                          updateQuestion(idx, { question_type: v as any })
                        }
                      >
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="multiple_choice">Multiple Choice</SelectItem>
                          <SelectItem value="rating">Rating (1–5)</SelectItem>
                          <SelectItem value="text">Open Text</SelectItem>
                          <SelectItem value="number">Number</SelectItem>
                          <SelectItem value="currency">Currency</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex items-center gap-2 pt-6">
                      <Switch
                        checked={q.required}
                        onCheckedChange={(c) => updateQuestion(idx, { required: c })}
                      />
                      <Label className="text-sm">Required</Label>
                    </div>
                  </div>

                  {draft.survey_type === "impact" && (
                    <div className="space-y-1">
                      <Label className="text-xs">Impact Metric Key</Label>
                      <Input
                        value={q.impact_metric_key || ""}
                        onChange={(e) =>
                          updateQuestion(idx, { impact_metric_key: e.target.value })
                        }
                        placeholder="e.g. revenue_generated"
                      />
                    </div>
                  )}

                  {q.question_type === "multiple_choice" && (
                    <div className="space-y-2">
                      <Label className="text-xs">Answer Options</Label>
                      {(q.options || []).map((opt, oIdx) => (
                        <div key={oIdx} className="flex items-center gap-2">
                          <Input
                            value={opt}
                            onChange={(e) => {
                              const next = [...(q.options || [])];
                              next[oIdx] = e.target.value;
                              updateQuestion(idx, { options: next });
                            }}
                            placeholder={`Option ${oIdx + 1}`}
                          />
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => {
                              const next = (q.options || []).filter((_, i) => i !== oIdx);
                              updateQuestion(idx, { options: next });
                            }}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() =>
                          updateQuestion(idx, {
                            options: [...(q.options || []), ""],
                          })
                        }
                      >
                        <Plus className="h-3 w-3 mr-1" /> Add Option
                      </Button>
                    </div>
                  )}
                </div>
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() => removeQuestion(idx)}
                  title="Delete question"
                >
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              </div>
            </div>
          ))}

          <Button variant="outline" onClick={addQuestion}>
            <Plus className="h-4 w-4 mr-1" /> Add Question
          </Button>
        </CardContent>
      </Card>

      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          disabled={saving}
          style={{ backgroundColor: ORANGE, color: "white" }}
          className="hover:opacity-90"
        >
          {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
          Save Survey
        </Button>
      </div>

      <AlertDialog
        open={!!answerLossWarning}
        onOpenChange={(o) => !o && setAnswerLossWarning(null)}
      >
        <AlertDialogContent style={FONT}>
          <AlertDialogHeader>
            <AlertDialogTitle>This will delete submitted answers</AlertDialogTitle>
            <AlertDialogDescription>
              {answerLossWarning} This cannot be undone. Existing responses will
              remain but will no longer have answers for the removed questions.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep the questions</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setAnswerLossWarning(null);
                void performSave(true);
              }}
              style={{ backgroundColor: "#dc2626", color: "white" }}
            >
              Delete answers & save
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmActivate} onOpenChange={setConfirmActivate}>
        <AlertDialogContent style={FONT}>
          <AlertDialogHeader>
            <AlertDialogTitle>Activate this survey?</AlertDialogTitle>
            <AlertDialogDescription>
              You are changing this survey from <strong>Draft</strong> to <strong>Active</strong>.
              Once active, assigned users will see the banner and modal and can submit responses.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setConfirmActivate(false);
                void performSave();
              }}
              style={{ backgroundColor: ORANGE, color: "white" }}
            >
              Activate & Save
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

/* ===================== Send Wave Dialog ===================== */

type CohortLite = { id: string; name: string };
type UserLite = { id: string; full_name: string | null; role: string; email: string | null };

const ROLE_LABEL: Record<string, string> = {
  entrepreneur: "Entrepreneurs",
  mentor: "Mentors",
  both: "Both",
};
const MODE_LABEL: Record<string, string> = {
  all: "All",
  cohort: "Specific Cohort",
  individual: "Individual Users",
};

function SendWaveDialog({
  survey,
  onClose,
}: {
  survey: PlatformSurvey | null;
  onClose: () => void;
}) {
  const open = !!survey;
  const sendMut = useSendPlatformSurveyWave();
  const qc = useQueryClient();
  const [localStatus, setLocalStatus] = useState<PlatformSurvey["status"] | null>(null);
  const [activating, setActivating] = useState(false);
  const effectiveStatus = localStatus ?? survey?.status ?? "draft";

  useEffect(() => {
    setLocalStatus(null);
  }, [survey?.id]);

  const handleActivate = async () => {
    if (!survey) return;
    setActivating(true);
    try {
      const { error } = await supabase
        .from("platform_surveys")
        .update({ status: "active" })
        .eq("id", survey.id);
      if (error) throw error;
      setLocalStatus("active");
      qc.invalidateQueries({ queryKey: ["platform_surveys"] });
      toast.success("Survey activated.");
    } catch (e: any) {
      console.error("Full Supabase error:", JSON.stringify(e, null, 2));
      toast.error("Failed to activate survey.");
    } finally {
      setActivating(false);
    }
  };

  const [waveLabel, setWaveLabel] = useState("");
  const [dueAt, setDueAt] = useState<string>("");
  const [cohortId, setCohortId] = useState<string>("");
  const [cohorts, setCohorts] = useState<CohortLite[]>([]);
  const [skipDuplicates, setSkipDuplicates] = useState(true);

  const [userSearch, setUserSearch] = useState("");
  const [users, setUsers] = useState<UserLite[]>([]);
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);

  // Reset on open/close
  useEffect(() => {
    if (open) {
      setWaveLabel("");
      setDueAt("");
      setCohortId("");
      setSkipDuplicates(true);
      setUserSearch("");
      setSelectedUserIds([]);
    }
  }, [open]);

  // Load cohorts when needed
  useEffect(() => {
    if (!open || survey?.targeting_mode !== "cohort") return;
    (async () => {
      const { data, error } = await supabase
        .from("cohorts")
        .select("id, name")
        .order("name", { ascending: true });
      if (error) {
        console.error("Full Supabase error:", JSON.stringify(error, null, 2));
        return;
      }
      setCohorts((data || []) as CohortLite[]);
    })();
  }, [open, survey?.targeting_mode]);

  // Load users when individual targeting
  useEffect(() => {
    if (!open || survey?.targeting_mode !== "individual" || !survey) return;
    let cancelled = false;
    (async () => {
      setUsersLoading(true);
      let query = supabase
        .from("profiles")
        .select("id, full_name, role")
        .order("full_name", { ascending: true })
        .limit(50);

      if (survey.target_role === "entrepreneur") {
        query = query.eq("role", "entrepreneur");
      } else if (survey.target_role === "mentor") {
        query = query.eq("role", "mentor");
      } else {
        query = query.in("role", ["entrepreneur", "mentor"]);
      }

      const term = userSearch.trim();
      if (term.length > 0) {
        query = query.ilike("full_name", `%${term}%`);
      }

      const { data, error } = await query;
      if (cancelled) return;
      if (error) {
        console.error("Full Supabase error:", JSON.stringify(error, null, 2));
        setUsers([]);
      } else {
        setUsers(
          (data || []).map((p: any) => ({
            id: p.id,
            full_name: p.full_name,
            role: p.role,
            email: null,
          }))
        );
      }
      setUsersLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [open, survey, userSearch]);

  if (!survey) return null;

  const targetingDisplay = `${MODE_LABEL[survey.targeting_mode]} — ${ROLE_LABEL[survey.target_role]}`;

  const toggleUser = (id: string) => {
    setSelectedUserIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleSend = async () => {
    if (!waveLabel.trim()) {
      toast.error("Wave label is required.");
      return;
    }
    if (survey.targeting_mode === "cohort" && !cohortId) {
      toast.error("Please select a cohort.");
      return;
    }
    if (survey.targeting_mode === "individual" && selectedUserIds.length === 0) {
      toast.error("Please select at least one user.");
      return;
    }

    try {
      // 1. Resolve target user IDs
      let targetIds: string[] = [];

      if (survey.targeting_mode === "all") {
        const roles: ("entrepreneur" | "mentor")[] =
          survey.target_role === "both"
            ? ["entrepreneur", "mentor"]
            : [survey.target_role as "entrepreneur" | "mentor"];
        const { data, error } = await supabase
          .from("profiles")
          .select("id")
          .in("role", roles);
        if (error) throw error;
        targetIds = (data || []).map((r: any) => r.id);
      } else if (survey.targeting_mode === "cohort") {
        // cohort membership lives on course_enrollments.cohort_id -> user_id
        const { data: enrollments, error: enrollErr } = await supabase
          .from("course_enrollments")
          .select("user_id")
          .eq("cohort_id", cohortId);
        if (enrollErr) throw enrollErr;
        const uids = Array.from(new Set((enrollments || []).map((r: any) => r.user_id)));

        if (uids.length === 0) {
          targetIds = [];
        } else if (survey.target_role === "both") {
          targetIds = uids;
        } else {
          const { data: filtered, error: profErr } = await supabase
            .from("profiles")
            .select("id")
            .in("id", uids)
            .eq("role", survey.target_role);
          if (profErr) throw profErr;
          targetIds = (filtered || []).map((r: any) => r.id);
        }
      } else {
        targetIds = [...selectedUserIds];
      }

      // 2. Skip duplicates
      if (skipDuplicates && targetIds.length > 0) {
        const { data: prior, error: priorErr } = await supabase
          .from("platform_survey_responses")
          .select("user_id")
          .eq("survey_id", survey.id)
          .not("completed_at", "is", null);
        if (priorErr) throw priorErr;
        const completed = new Set((prior || []).map((r: any) => r.user_id));
        targetIds = targetIds.filter((id) => !completed.has(id));
      }

      if (targetIds.length === 0) {
        toast.error("No eligible users to send to.");
        return;
      }

      const { count } = await sendMut.mutateAsync({
        surveyId: survey.id,
        waveLabel: waveLabel.trim(),
        dueAt: dueAt || null,
        targetUserIds: targetIds,
        cohortId: survey.targeting_mode === "cohort" ? cohortId : null,
      });

      toast.success(`Wave sent. ${count} users assigned.`);
      onClose();
    } catch (e: any) {
      console.error("Full Supabase error:", JSON.stringify(e, null, 2));
      toast.error("Failed to send wave. Please try again.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent style={FONT} className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Send Wave — {survey.title}</DialogTitle>
          <DialogDescription>
            Dispatch this survey to users and notify them by email.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {effectiveStatus === "draft" && (
            <div
              className="rounded-md border p-3 flex items-start gap-2"
              style={{ backgroundColor: "#fffbeb", borderColor: "#f59e0b" }}
            >
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" style={{ color: "#b45309" }} />
              <div className="flex-1 space-y-2">
                <p className="text-sm" style={{ color: "#78350f" }}>
                  This survey is in <strong>draft</strong>. Assigned users will not see the
                  banner or modal until it is activated.
                </p>
                <Button
                  size="sm"
                  onClick={handleActivate}
                  disabled={activating}
                  style={{ backgroundColor: "#f59e0b", color: "white" }}
                  className="hover:opacity-90"
                >
                  {activating && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  Activate Survey Now
                </Button>
              </div>
            </div>
          )}

          <div className="space-y-1">
            <Label>Wave Label *</Label>
            <Input
              value={waveLabel}
              onChange={(e) => setWaveLabel(e.target.value)}
              placeholder="e.g. 2026-Q1"
            />
            <p className="text-xs text-muted-foreground">
              This label identifies the response period and appears in results.
            </p>
          </div>

          <div className="space-y-1">
            <Label>Due Date</Label>
            <Input
              type="date"
              value={dueAt}
              onChange={(e) => setDueAt(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Users will see this as their response deadline.
            </p>
          </div>

          <div className="space-y-1">
            <Label>Targeting</Label>
            <p className="text-sm font-medium">{targetingDisplay}</p>
          </div>

          {survey.targeting_mode === "cohort" && (
            <div className="space-y-1">
              <Label>Cohort *</Label>
              <Select value={cohortId} onValueChange={setCohortId}>
                <SelectTrigger><SelectValue placeholder="Select a cohort" /></SelectTrigger>
                <SelectContent>
                  {cohorts.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {survey.targeting_mode === "individual" && (
            <div className="space-y-2">
              <Label>Users *</Label>
              <Input
                placeholder="Search by name…"
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
              />
              <div
                className="border rounded-md overflow-y-auto"
                style={{ maxHeight: 200 }}
              >
                {usersLoading ? (
                  <div className="p-3 text-sm text-muted-foreground">Loading…</div>
                ) : users.length === 0 ? (
                  <div className="p-3 text-sm text-muted-foreground">No users found.</div>
                ) : (
                  <ul className="divide-y">
                    {users.map((u) => (
                      <li key={u.id} className="flex items-center gap-2 p-2">
                        <Checkbox
                          checked={selectedUserIds.includes(u.id)}
                          onCheckedChange={() => toggleUser(u.id)}
                          id={`user-${u.id}`}
                        />
                        <label
                          htmlFor={`user-${u.id}`}
                          className="text-sm cursor-pointer flex-1"
                        >
                          {u.full_name || "Unnamed"} — {labelize(u.role)}
                        </label>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              {selectedUserIds.length > 0 && (
                <p className="text-xs text-muted-foreground">
                  {selectedUserIds.length} selected
                </p>
              )}
            </div>
          )}

          <div className="flex items-center gap-2 pt-1">
            <Checkbox
              id="skip-duplicates"
              checked={skipDuplicates}
              onCheckedChange={(c) => setSkipDuplicates(!!c)}
            />
            <label htmlFor="skip-duplicates" className="text-sm cursor-pointer">
              Skip users who have already completed this survey in a previous wave
            </label>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={sendMut.isPending}>
            Cancel
          </Button>
          <Button
            onClick={handleSend}
            disabled={sendMut.isPending}
            style={{ backgroundColor: "#C94F13", color: "white" }}
            className="hover:opacity-90"
          >
            {sendMut.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Send Wave
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ===================== Results View ===================== */

function StatTile({
  label,
  value,
  accent,
}: {
  label: string;
  value: string | number;
  accent?: string;
}) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="text-xs uppercase tracking-wide text-muted-foreground">
          {label}
        </div>
        <div
          className="text-2xl font-bold mt-1"
          style={{ color: accent || undefined }}
        >
          {value}
        </div>
      </CardContent>
    </Card>
  );
}

function getChoice(a: SurveyAnswer): string | null {
  if (!a.answer_choice) return null;
  if (typeof a.answer_choice === "string") return a.answer_choice;
  if (typeof a.answer_choice === "object" && "selected" in a.answer_choice) {
    return String(a.answer_choice.selected);
  }
  return null;
}

function MultipleChoiceChart({
  question,
  answers,
}: {
  question: PlatformSurveyQuestion;
  answers: SurveyAnswer[];
}) {
  const opts = (Array.isArray(question.options) ? question.options : []) as string[];
  const total = answers.length;
  const data = opts.map((opt) => {
    const count = answers.filter((a) => getChoice(a) === opt).length;
    const pct = total > 0 ? Math.round((count / total) * 100) : 0;
    return { option: opt, count, label: `${count} (${pct}%)` };
  });

  if (opts.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">No options configured.</p>
    );
  }

  return (
    <div style={{ width: "100%", height: Math.max(120, opts.length * 48) }}>
      <ResponsiveContainer>
        <BarChart data={data} layout="vertical" margin={{ left: 20, right: 60 }}>
          <XAxis type="number" allowDecimals={false} hide />
          <YAxis
            type="category"
            dataKey="option"
            width={140}
            tick={{ fontSize: 12 }}
          />
          <RTooltip />
          <Bar dataKey="count" fill={ORANGE} radius={[0, 4, 4, 0]}>
            <LabelList dataKey="label" position="right" style={{ fontSize: 12 }} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function RatingChart({ answers }: { answers: SurveyAnswer[] }) {
  const buckets = [1, 2, 3, 4, 5].map((r) => ({
    rating: String(r),
    count: answers.filter((a) => a.answer_number === r).length,
  }));
  const nums = answers
    .map((a) => a.answer_number)
    .filter((n): n is number => typeof n === "number");
  const avg = nums.length > 0 ? nums.reduce((s, n) => s + n, 0) / nums.length : 0;

  return (
    <div className="space-y-2">
      <div style={{ width: "100%", height: 200 }}>
        <ResponsiveContainer>
          <BarChart data={buckets}>
            <XAxis dataKey="rating" />
            <YAxis allowDecimals={false} />
            <RTooltip />
            <Bar dataKey="count" fill={BLUE} radius={[4, 4, 0, 0]}>
              <LabelList dataKey="count" position="top" style={{ fontSize: 12 }} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="text-sm font-medium">
        Average: {avg.toFixed(1)} / 5
      </div>
    </div>
  );
}

function NumberStats({
  answers,
  isCurrency,
}: {
  answers: SurveyAnswer[];
  isCurrency: boolean;
}) {
  const nums = answers
    .map((a) => a.answer_number)
    .filter((n): n is number => typeof n === "number");
  const fmt = (n: number) =>
    isCurrency
      ? `$${n.toLocaleString(undefined, { maximumFractionDigits: 2 })}`
      : n.toLocaleString();
  const avg = nums.length > 0 ? nums.reduce((s, n) => s + n, 0) / nums.length : 0;
  const min = nums.length > 0 ? Math.min(...nums) : 0;
  const max = nums.length > 0 ? Math.max(...nums) : 0;
  const total = nums.reduce((s, n) => s + n, 0);

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      <StatTile label="Average" value={fmt(avg)} accent={GREEN} />
      <StatTile label="Min" value={fmt(min)} />
      <StatTile label="Max" value={fmt(max)} />
      <StatTile
        label={isCurrency ? "Total Reported" : "Total"}
        value={fmt(total)}
      />
    </div>
  );
}

function TextAnswers({ answers }: { answers: SurveyAnswer[] }) {
  const [showAll, setShowAll] = useState(false);
  const filtered = answers.filter(
    (a) => a.answer_text && a.answer_text.trim() !== ""
  );
  const visible = showAll ? filtered : filtered.slice(0, 20);

  if (filtered.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">No text responses yet.</p>
    );
  }

  return (
    <div className="space-y-2">
      <ScrollArea className="max-h-[320px] pr-2">
        <div className="space-y-2">
          {visible.map((a) => {
            const name = a.user_full_name || "Anonymous";
            const role = a.user_role
              ? a.user_role.charAt(0).toUpperCase() + a.user_role.slice(1)
              : "";
            return (
              <div
                key={a.id}
                className="border rounded-md p-3 text-sm"
              >
                <div className="text-xs text-muted-foreground mb-1">
                  {name}
                  {role ? ` — ${role}` : ""}
                </div>
                <div className="whitespace-pre-wrap">{a.answer_text}</div>
              </div>
            );
          })}
        </div>
      </ScrollArea>
      {!showAll && filtered.length > 20 && (
        <Button
          variant="link"
          size="sm"
          onClick={() => setShowAll(true)}
          className="px-0"
        >
          Show all {filtered.length} responses
        </Button>
      )}
    </div>
  );
}

function QuestionResultSection({
  question,
  answers,
}: {
  question: PlatformSurveyQuestion;
  answers: SurveyAnswer[];
}) {
  const answeredCount = answers.filter((a) => {
    if (question.question_type === "text")
      return !!a.answer_text && a.answer_text.trim() !== "";
    if (question.question_type === "multiple_choice")
      return getChoice(a) !== null;
    return a.answer_number !== null;
  }).length;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-1">
            <div className="font-bold text-base">{question.question_text}</div>
            <div className="text-xs text-muted-foreground">
              {labelize(question.question_type)}
            </div>
            {question.impact_metric_key && (
              <div className="text-xs italic text-muted-foreground">
                Metric: {question.impact_metric_key}
              </div>
            )}
          </div>
          <div className="text-xs text-muted-foreground whitespace-nowrap">
            N = {answeredCount} answered
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {answeredCount === 0 ? (
          <p className="text-sm text-muted-foreground">
            No responses yet for this question.
          </p>
        ) : question.question_type === "multiple_choice" ? (
          <MultipleChoiceChart question={question} answers={answers} />
        ) : question.question_type === "rating" ? (
          <RatingChart answers={answers} />
        ) : question.question_type === "number" ? (
          <NumberStats answers={answers} isCurrency={false} />
        ) : question.question_type === "currency" ? (
          <NumberStats answers={answers} isCurrency={true} />
        ) : (
          <TextAnswers answers={answers} />
        )}
      </CardContent>
    </Card>
  );
}

function SurveyResults({
  survey,
  onClose,
}: {
  survey: PlatformSurvey;
  onClose: () => void;
}) {
  const [waveLabel, setWaveLabel] = useState<string>("__all__");
  const [roleFilter, setRoleFilter] = useState<string>("__all__");
  const [aggregateMode, setAggregateMode] = useState(false);
  const [selectedSurveyIds, setSelectedSurveyIds] = useState<string[]>([]);

  const groupId = survey.survey_group_id || survey.id;
  const hasGroup = !!survey.survey_group_id;

  const groupQuery = useSurveysInGroup(hasGroup ? groupId : null);

  // Initialize selected surveys when entering aggregate mode
  useEffect(() => {
    if (aggregateMode && groupQuery.data?.surveys) {
      setSelectedSurveyIds(groupQuery.data.surveys.map((s) => s.id));
    }
  }, [aggregateMode, groupQuery.data]);

  const singleQuery = useSurveyResults(aggregateMode ? null : survey.id, {
    waveLabel,
    role: roleFilter,
  });
  const aggQuery = useAggregateSurveyResults(
    aggregateMode ? groupId : null,
    selectedSurveyIds,
    { role: roleFilter }
  );

  const activeQuery = aggregateMode ? aggQuery : singleQuery;
  const data = activeQuery.data;
  const isLoading = activeQuery.isLoading;
  const error = activeQuery.error;

  // Count excluded questions for aggregate
  const excludedCount = aggregateMode
    ? // Difference between merged groups and a representative single survey's question count
      0
    : 0;

  return (
    <div className="space-y-6" style={FONT}>
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={onClose}>
          <ArrowLeft className="h-4 w-4 mr-1" /> Back to Surveys
        </Button>
      </div>

      <div className="space-y-2">
        <div className="flex items-center gap-3 flex-wrap">
          <h2 className="text-2xl font-bold">{survey.title}</h2>
          <TypeBadge type={survey.survey_type} />
          <StatusBadge status={survey.status} />
        </div>
        {survey.description && (
          <p className="text-sm text-muted-foreground">{survey.description}</p>
        )}
      </div>

      {/* Summary stats */}
      {isLoading || !data ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-20 w-full" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatTile label="Assigned" value={data.stats.assigned} />
          <StatTile
            label="Completed"
            value={data.stats.completed}
            accent={GREEN}
          />
          <StatTile
            label="Response Rate"
            value={`${data.stats.responseRate.toFixed(0)}%`}
            accent={ORANGE}
          />
          <StatTile label="Waves" value={data.stats.waveCount} />
        </div>
      )}

      {/* Aggregate toggle */}
      {hasGroup && (
        <Card>
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center gap-3">
              <Switch
                checked={aggregateMode}
                onCheckedChange={setAggregateMode}
              />
              <Label className="font-medium">Aggregate View</Label>
              <span className="text-xs text-muted-foreground">
                Merge results across surveys in the same group
              </span>
            </div>

            {aggregateMode && groupQuery.data?.surveys && (
              <div className="space-y-2 pl-2">
                <div className="text-sm font-medium">Surveys included:</div>
                <div className="space-y-1">
                  {groupQuery.data.surveys.map((s) => (
                    <label
                      key={s.id}
                      className="flex items-center gap-2 text-sm"
                    >
                      <Checkbox
                        checked={selectedSurveyIds.includes(s.id)}
                        onCheckedChange={(checked) => {
                          setSelectedSurveyIds((prev) =>
                            checked
                              ? [...prev, s.id]
                              : prev.filter((id) => id !== s.id)
                          );
                        }}
                      />
                      <span>
                        {s.title}
                        {s.published_at && (
                          <span className="text-muted-foreground ml-1">
                            ({new Date(s.published_at).toLocaleDateString()})
                          </span>
                        )}
                      </span>
                    </label>
                  ))}
                </div>
                {excludedCount > 0 && (
                  <p className="text-xs text-muted-foreground italic">
                    Some questions are excluded from aggregate view because they were added after the original survey.
                  </p>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-end">
        {!aggregateMode && data && data.waves.length > 1 && (
          <div className="space-y-1">
            <Label className="text-xs">Wave</Label>
            <Select value={waveLabel} onValueChange={setWaveLabel}>
              <SelectTrigger className="w-[200px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All Waves</SelectItem>
                {data.waves.map((w) => (
                  <SelectItem key={w} value={w}>
                    {w}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {survey.target_role === "both" && (
          <div className="space-y-1">
            <Label className="text-xs">Role</Label>
            <Select value={roleFilter} onValueChange={setRoleFilter}>
              <SelectTrigger className="w-[200px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All</SelectItem>
                <SelectItem value="entrepreneur">Entrepreneurs</SelectItem>
                <SelectItem value="mentor">Mentors</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {error && (
        <div className="text-sm text-red-500 p-4 border border-red-200 rounded-md">
          Failed to load survey results. Please try refreshing.
        </div>
      )}

      {/* Per-question charts */}
      {isLoading ? (
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} className="h-48 w-full" />
          ))}
        </div>
      ) : !data || data.questions.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center text-muted-foreground">
            {aggregateMode
              ? "No questions with shared group keys across selected surveys."
              : "This survey has no questions yet."}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {data.questions.map((q) => (
            <QuestionResultSection
              key={q.id}
              question={q}
              answers={data.answersByQuestionId[q.id] || []}
            />
          ))}
        </div>
      )}
    </div>
  );
}
